'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import useRouteState from 'src/hooks/routes/useRouteState';
import useRoutePreview from 'src/hooks/routes/useRoutePreview';
import type {
  EnrichedRouteStop,
  RouteDirections,
} from 'src/hooks/routes/types';
import { useAppSelector } from 'src/redux/hooks';
import {
  selectHoveredLegIndex,
  selectPreviewRoute,
} from 'src/redux/slices/routesSlice';
import { RouteLayer } from 'src/components/Routes/layers/RouteLayer';
import type { RoutePathData } from 'src/components/Routes/layers/RouteLayer';
import { StopMarkers } from 'src/components/Routes/layers/StopMarkers';
import { ROUTE_BOUNDS_PADDING, SINGLE_STOP_ZOOM } from './constants';

type RouteBounds = NonNullable<RouteDirections['bounds']>;
type Rgba = [number, number, number, number];

const LEG_COLOR: Rgba = [14, 165, 233, 200]; // sky-500
const HOVERED_LEG_COLOR: Rgba = [2, 132, 199, 255]; // sky-600, full opacity
const DIMMED_LEG_COLOR: Rgba = [14, 165, 233, 80]; // sky-500, low opacity (also straight-line legs with no road path)

/** The box around the stops that have a position; null when none do. */
const getStopsBounds = (stops: EnrichedRouteStop[]): RouteBounds | null => {
  const located = stops.filter(
    (stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lng)
  );
  if (located.length === 0) return null;

  return {
    northeast: {
      lat: Math.max(...located.map((stop) => stop.lat)),
      lng: Math.max(...located.map((stop) => stop.lng)),
    },
    southwest: {
      lat: Math.min(...located.map((stop) => stop.lat)),
      lng: Math.min(...located.map((stop) => stop.lng)),
    },
  };
};

/**
 * Fits the map viewport to the route bounds when they change. Bounds around a
 * single point centre on it at a fixed zoom instead, since fitting to a point
 * zooms in as far as the map goes.
 * Must be rendered inside a <Map> component.
 */
const FitRouteBounds = ({ bounds }: { bounds: RouteBounds | null }) => {
  const map = useMap();
  const prevBoundsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!map) return;
    if (!bounds) {
      // Reopening the same route clears directions first; forget the last fit
      // so the map moves back to it.
      prevBoundsRef.current = null;
      return;
    }

    const { northeast, southwest } = bounds;
    const boundsKey = `${northeast.lat},${northeast.lng},${southwest.lat},${southwest.lng}`;

    // Only fit when bounds actually change
    if (boundsKey === prevBoundsRef.current) return;
    prevBoundsRef.current = boundsKey;

    if (northeast.lat === southwest.lat && northeast.lng === southwest.lng) {
      map.panTo({ lat: northeast.lat, lng: northeast.lng });
      map.setZoom(SINGLE_STOP_ZOOM);
      return;
    }

    map.fitBounds(
      {
        north: northeast.lat,
        south: southwest.lat,
        east: northeast.lng,
        west: southwest.lng,
      },
      ROUTE_BOUNDS_PADDING
    );
  }, [map, bounds]);

  return null;
};

interface ActiveRouteOverlayProps {
  /**
   * Also draw the route previewed with `setPreviewRoute` (Scout's "View Route
   * on Map") when no route is open in the routes drawer. Off by default so a
   * map that doesn't offer the preview never draws it.
   */
  showPreview?: boolean;
}

/**
 * Draws the route in the routes drawer on the map: the route line, a marker per
 * stop, and a viewport fit to the route (to the stops, for a preview without
 * directions). Renders nothing until a route is
 * being created, edited or viewed, or previewed when `showPreview` is on. A
 * route open in the drawer wins over the preview. Must be rendered inside a
 * <Map> component.
 */
const ActiveRouteOverlay = ({
  showPreview = false,
}: ActiveRouteOverlayProps) => {
  const {
    isCreating,
    isEditing,
    isViewing,
    tempStops,
    directions: drawerDirections,
  } = useRouteState();
  const hoveredLegIndex = useAppSelector(selectHoveredLegIndex);
  const previewRoute = useAppSelector(selectPreviewRoute);
  const isRouteActive = isCreating || isEditing || isViewing;
  const preview = useRoutePreview(
    showPreview && !isRouteActive ? (previewRoute?.id ?? null) : null
  );
  const stops = isRouteActive ? tempStops : preview.stops;
  const directions = isRouteActive ? drawerDirections : preview.directions;

  // A previewed route has no directions when it is a single stop or its
  // request failed, so fit to its stops to keep the markers on screen. The
  // drawer's route doesn't, so the map doesn't jump while a route is built.
  const stopsBounds = useMemo(
    () => (isRouteActive ? null : getStopsBounds(stops)),
    [isRouteActive, stops]
  );
  const fitBounds = directions?.bounds ?? stopsBounds;

  // Per-leg paths, so hovering a leg in the directions list can highlight it.
  const routePathData = useMemo<RoutePathData[]>(() => {
    if (!directions?.legs || directions.legs.length === 0) {
      return [];
    }

    const hasLegPolylines = directions.legs.some(
      (leg) => leg.polylinePoints && leg.polylinePoints.length > 0
    );

    if (hasLegPolylines) {
      return directions.legs
        .filter((leg) => leg.polylinePoints && leg.polylinePoints.length > 0)
        .map((leg, index) => ({
          id: `route-leg-${index}`,
          path: leg.polylinePoints.map(
            (p) => [p.lng, p.lat] as [number, number]
          ),
          color: leg.isApproximate
            ? DIMMED_LEG_COLOR
            : hoveredLegIndex === null
              ? LEG_COLOR
              : hoveredLegIndex === index
                ? HOVERED_LEG_COLOR
                : DIMMED_LEG_COLOR,
        }));
    }

    // Fallback: single polyline from the full route
    if (directions.polylinePoints && directions.polylinePoints.length > 0) {
      return [
        {
          id: 'active-route',
          path: directions.polylinePoints.map(
            (p) => [p.lng, p.lat] as [number, number]
          ),
          color: LEG_COLOR,
        },
      ];
    }

    return [];
  }, [directions, hoveredLegIndex]);

  return (
    <>
      <FitRouteBounds bounds={fitBounds} />
      {routePathData.length > 0 && <RouteLayer routeData={routePathData} />}
      {stops.length > 0 && <StopMarkers stops={stops} />}
    </>
  );
};

export default ActiveRouteOverlay;
